import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';

import axios from 'axios';
import EventModal from './EventModal';
import 'react-big-calendar/lib/css/react-big-calendar.css'; // Import default styles
import Page from '../../../components/Page';

const localizer = momentLocalizer(moment);

const RootStyle = styled('div')(({ theme }) => ({
    display: 'flex',
    height: '100vh',
}));

const Sidebar = styled('div')(({ theme }) => ({
    width: '230px',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    boxShadow: theme.shadows[3],
    borderRadius: theme.shape.borderRadius,
    overflowY: 'auto', // Allow scrolling if content overflows
    [theme.breakpoints.down('sm')]: {
        display: 'none', // Hide sidebar on small screens
    },
}));

const ContentStyle = styled('div')(({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(-4),
}));

const StyledCalendar = styled(Calendar)(({ theme }) => ({
    '& .rbc-calendar': {
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[3],
    },
    '& .rbc-toolbar': {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
        padding: theme.spacing(1),
    },
    '& .rbc-month-view': {
        '& .rbc-day-slot': {
            borderBottom: `1px solid ${theme.palette.divider}`,
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
            },
        },
        '& .rbc-day-slot.rbc-selected': {
            backgroundColor: theme.palette.primary.light,
            color: '#fff',
        },
    },
    '& .rbc-event': {
        backgroundColor: theme.palette.secondary.main,
        borderRadius: '5px',
        padding: '5px',
        color: '#fff',
        textAlign: 'center',
        whiteSpace: 'nowrap', // Prevent text wrapping
        overflow: 'hidden', // Hide overflow text
        textOverflow: 'ellipsis', // Show ellipsis for overflow text
    },
}));

export default function CalendarPage() {
    const [events, setEvents] = useState([]); // State to store events
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);


    const fetchEvents = async (date) => {
        try {
            const response = await axios.get('https://cpccbe.org/api/calender/get-events', {
                params: {
                    year: date.getFullYear(),
                    month: date.getMonth() + 1,
                }
            });

            const formattedEvents = response.data.map(event => ({
                ...event,
                start: new Date(event.start), // Ensure this is a Date object
                end: new Date(event.end),     // Ensure this is a Date object
            }));
            setEvents(formattedEvents);

        } catch (error) {
            console.error("Error fetching events:", error);
        }
    };

    useEffect(() => {
        const currentDate = new Date();
        fetchEvents(currentDate); // Fetch events for the current month on initial load
    }, []);

    const handleSelectEvent = (event) => {
        setSelectedEvent(event); // Set selected event for editing
        setModalOpen(true); // Open modal to edit existing event
    };

    const handleSelectSlot = ({ start, end }) => {
        setSelectedEvent({ start, end }); // Prepare for new event creation
        setModalOpen(true); // Open modal to add new event
    };

    const handleSaveEvent = async (eventData) => {
        try {
            if (selectedEvent && selectedEvent._id) {
                // Update existing event logic here
                await axios.put(`https://cpccbe.org/api/calender/update-event/${selectedEvent._id}`, {
                    ...eventData,
                    start: new Date(eventData.start), // Convert to Date object if necessary
                    end: new Date(eventData.end),     // Convert to Date object if necessary
                });
                setEvents((prev) =>
                    prev.map((event) =>
                        event._id === selectedEvent._id ? { ...event, ...eventData } : event
                    )
                );
            } else {
                // Add new event logic here
                const response = await axios.post('https://cpccbe.org/api/calender/add-event', {
                    ...eventData,
                    start: new Date(eventData.start), // Convert to Date object if necessary
                    end: new Date(eventData.end),     // Convert to Date object if necessary
                });
                setEvents((prev) => [...prev, response.data]); // Update local state with new event
            }

            setModalOpen(false); // Close modal after saving
            setSelectedEvent(null); // Clear selected event after saving
        } catch (error) {
            console.error("Error saving event:", error);
            alert("There was an error saving the event.");
        }
    };


    const handleDeleteEvent = async () => {
        if (!selectedEvent || !selectedEvent._id) return;

        if (window.confirm(`Are you sure you want to delete the event?`)) {
            try {
                await axios.delete(`https://cpccbe.org/api/calender/delete-event/${selectedEvent._id}`);

                setEvents((prev) => prev.filter((event) => event._id !== selectedEvent._id)); // Remove deleted event from local state

                setModalOpen(false); // Close modal after deletion
                setSelectedEvent(null); // Clear selected event
            } catch (error) {
                console.error("Error deleting event:", error);
                alert("There was an error deleting the event.");
            }
        }
    };

    const handleNavigate = (date) => {
        fetchEvents(date); // Fetch events when navigating months/years
    };


    return (
        <Page title="Calendar">
            <RootStyle>
                <Sidebar>
                    <Typography variant="h6" gutterBottom>
                        Events
                    </Typography>
                    {events.length > 0 ? (
                        [...events] // Create a copy of the array to avoid mutating state
                            .sort((a, b) => new Date(b.start) - new Date(a.start)) // Sort in descending order (newest first)
                            .map((event) => (
                                <Box key={event._id} sx={{ marginBottom: 1 }}>
                                    <Typography variant="body1">{event.title}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {new Date(event.start).toLocaleString()} - {new Date(event.end).toLocaleString()}
                                    </Typography>
                                </Box>
                            ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No events available.
                        </Typography>
                    )}

                </Sidebar>

                <ContentStyle>
                    <Typography variant="h4" gutterBottom align="center">
                        Calendar
                    </Typography>

                    <StyledCalendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: '100%', marginTop: '20px' }}
                        selectable
                        onSelectEvent={handleSelectEvent}
                        onSelectSlot={handleSelectSlot}
                        views={['month', 'week', 'day']} // Specify which views to show
                        onNavigate={handleNavigate} // Call fetchEvents when navigating months/years
                    />

                    {/* Modal for adding/editing events */}
                    <EventModal
                        open={modalOpen}
                        onClose={() => setModalOpen(false)}
                        eventData={selectedEvent}
                        onSave={handleSaveEvent}
                        handleDelete={handleDeleteEvent}
                    />
                </ContentStyle>
            </RootStyle>
        </Page>
    );
}